<template>
	<section>
		<loader :dialog="loaderDialog"></loader>
		<floating-alert :message="alertMessage" :show="alertShow" :type="alertType"></floating-alert>

		<v-card elevation="2" rounded="xl">
			<!-- ─── En-tête ─── -->
			<div class="cal-header">
				<div class="cal-header-left">
					<div class="cal-icon-box">
						<v-icon size="18" color="white">mdi-calendar-clock</v-icon>
					</div>
					<div>
						<p class="cal-title">Rappels</p>
						<p class="cal-subtitle">{{ viewTitle }}</p>
					</div>
				</div>

				<div class="cal-nav">
					<v-btn icon small @click="navigate(-1)">
						<v-icon>mdi-chevron-left</v-icon>
					</v-btn>
					<v-btn text small class="cal-today-btn" @click="goToday">Aujourd'hui</v-btn>
					<v-btn icon small @click="navigate(1)">
						<v-icon>mdi-chevron-right</v-icon>
					</v-btn>
				</div>

				<v-btn-toggle v-model="viewMode" mandatory dense class="cal-view-toggle">
					<v-btn value="month" small class="cal-view-btn">Mois</v-btn>
					<v-btn value="week" small class="cal-view-btn">Semaine</v-btn>
					<v-btn value="day" small class="cal-view-btn">Jour</v-btn>
				</v-btn-toggle>

				<v-spacer></v-spacer>

				<v-btn depressed rounded small class="cal-add-btn" @click="openCreate(viewDate)">
					<v-icon left x-small>mdi-plus</v-icon>
					Nouveau rappel
				</v-btn>
			</div>

			<v-divider></v-divider>

			<!-- Chargement -->
			<div v-if="loading" class="cal-loading">
				<v-progress-circular indeterminate size="32" color="#c62828"></v-progress-circular>
			</div>

			<div v-else>
				<!-- ─── Vue Mois ─── -->
				<div v-if="viewMode === 'month'" class="cal-month">
					<div class="cal-day-headers">
						<div v-for="d in dayNames" :key="d" class="cal-day-header">{{ d }}</div>
					</div>
					<div class="cal-grid">
						<div
							v-for="(cell, i) in monthGrid"
							:key="i"
							class="cal-cell"
							:class="{
								'cal-cell-other-month': cell.getMonth() !== viewDate.getMonth(),
								'cal-cell-today': isToday(cell),
							}"
							@click.self="openCreate(cell)"
						>
							<div
								class="cal-cell-num"
								:class="{ 'cal-cell-num-today': isToday(cell) }"
								@click="drillDown(cell)"
							>{{ cell.getDate() }}</div>
							<div class="cal-cell-events">
								<div
									v-for="(ev, ei) in eventsForDate(cell).slice(0, 2)"
									:key="ev._id + '-' + ei"
									class="cal-ev-chip"
									:class="{ 'cal-ev-chip-overdue': isOverdue(ev) }"
									:title="ev.title"
									@click.stop="openEdit(ev)"
								>
									<v-icon x-small class="mr-1" style="vertical-align:middle;opacity:0.7">mdi-clock-outline</v-icon>{{ ev.time }} {{ ev.title }}
								</div>
								<div
									v-if="eventsForDate(cell).length > 2"
									class="cal-ev-more"
									@click.stop="drillDown(cell)"
								>+{{ eventsForDate(cell).length - 2 }} autre{{ eventsForDate(cell).length - 2 > 1 ? 's' : '' }}</div>
							</div>
						</div>
					</div>
				</div>

				<!-- ─── Vue Semaine ─── -->
				<div v-else-if="viewMode === 'week'" class="cal-week">
					<div class="cal-week-grid">
						<div
							v-for="(day, di) in weekDays"
							:key="di"
							class="cal-week-col"
							:class="{ 'cal-week-col-today': isToday(day) }"
						>
							<div class="cal-week-col-head" @click="openCreate(day)">
								<span class="cal-week-dow">{{ shortDayName(day) }}</span>
								<span class="cal-week-dnum" :class="{ 'cal-week-dnum-today': isToday(day) }">{{ day.getDate() }}</span>
							</div>
							<div class="cal-week-events">
								<div
									v-for="ev in eventsForDate(day)"
									:key="ev._id"
									class="cal-week-ev"
									:class="{ 'cal-week-ev-overdue': isOverdue(ev) }"
									@click="openEdit(ev)"
								>
									<span class="cal-week-ev-time">{{ ev.time }}</span>
									<span class="cal-week-ev-title">{{ ev.title }}</span>
								</div>
								<div v-if="eventsForDate(day).length === 0" class="cal-week-no-ev">—</div>
							</div>
						</div>
					</div>
				</div>

				<!-- ─── Vue Jour ─── -->
				<div v-else class="cal-day-view">
					<div class="cal-day-head">
						<p class="cal-day-head-label">{{ formatFullDate(viewDate) }}</p>
					</div>
					<div v-if="dayEvents.length === 0" class="cal-day-empty">
						<v-icon size="44" color="#e5e7eb">mdi-calendar-blank-outline</v-icon>
						<p class="cal-day-empty-text">Aucun rappel ce jour</p>
						<v-btn text small color="#c62828" @click="openCreate(viewDate)">
							<v-icon left x-small>mdi-plus</v-icon>Ajouter un rappel
						</v-btn>
					</div>
					<div v-else class="cal-day-list">
						<div
							v-for="ev in dayEvents"
							:key="ev._id"
							class="cal-day-ev"
							:class="{ 'cal-day-ev-overdue': isOverdue(ev) }"
						>
							<div class="cal-day-ev-time-col">
								<span class="cal-day-ev-time">{{ ev.time }}</span>
							</div>
							<div class="cal-day-ev-body">
								<p class="cal-day-ev-title">{{ ev.title }}</p>
								<p v-if="ev.description" class="cal-day-ev-desc">{{ ev.description }}</p>
								<div class="cal-day-ev-meta">
									<v-chip v-if="ev.salle && ev.salle.name" x-small color="#f3f4f6" text-color="#374151" class="mr-1">
										<v-icon left x-small>mdi-domain</v-icon>{{ ev.salle.name }}
									</v-chip>
									<v-chip v-if="ev.recurrence && ev.recurrence !== 'none'" x-small color="#dbeafe" text-color="#1e40af" class="mr-1">
										<v-icon left x-small>mdi-repeat</v-icon>{{ recurrenceLabel(ev.recurrence) }}
									</v-chip>
								</div>
							</div>
							<div class="cal-day-ev-actions">
								<v-btn icon small @click="openEdit(ev)"><v-icon size="16" color="#374151">mdi-pencil</v-icon></v-btn>
								<v-btn icon small @click="confirmArchive(ev)">
									<v-icon size="16" color="#6b7280">mdi-archive-outline</v-icon>
								</v-btn>
								<v-btn icon small @click="confirmDelete(ev)">
									<v-icon size="16" color="#c62828">mdi-delete</v-icon>
								</v-btn>
							</div>
						</div>
					</div>
				</div>
			</div>
		</v-card>

		<!-- ─── Dialog : Créer / Modifier ─── -->
		<v-dialog v-model="dialog" max-width="520px">
			<v-card rounded="xl" class="pa-2">
				<div class="form-dialog-header">
					<div class="form-dialog-icon" style="background:#c62828;">
						<v-icon size="14" color="white">mdi-calendar-clock</v-icon>
					</div>
					<div>
						<p class="form-dialog-title">{{ editedId ? 'Modifier le rappel' : 'Nouveau rappel' }}</p>
						<p class="form-dialog-sub">Rappel</p>
					</div>
					<v-spacer></v-spacer>
					<v-btn icon small @click="closeDialog"><v-icon color="#6b7280">mdi-close</v-icon></v-btn>
				</div>
				<v-divider></v-divider>
				<v-card-text class="pt-5">
					<v-form ref="form" v-model="valid" lazy-validation>
						<v-text-field
							v-model="form.title"
							label="Titre"
							outlined dense rounded
							prepend-inner-icon="mdi-text"
							:rules="[v => !!v || 'Champ requis']"
							hide-details="auto" class="mb-4"
						></v-text-field>
						<v-textarea
							v-model="form.description"
							label="Description (optionnel)"
							outlined dense rounded
							prepend-inner-icon="mdi-note-text"
							rows="2" hide-details class="mb-4"
						></v-textarea>
						<v-autocomplete
							v-model="form.salle"
							:items="venueOptions"
							item-text="name"
							item-value="_id"
							label="Salle (optionnel)"
							outlined dense rounded
							prepend-inner-icon="mdi-domain"
							clearable hide-details class="mb-4"
							:filter="() => true"
						></v-autocomplete>
						<v-row>
							<v-col cols="6">
								<v-menu v-model="dateMenu" :close-on-content-click="false">
									<template v-slot:activator="{ on, attrs }">
										<v-text-field
											v-model="form.date"
											label="Date"
											outlined dense rounded
											prepend-inner-icon="mdi-calendar"
											readonly
											:rules="[v => !!v || 'Champ requis']"
											hide-details="auto"
											v-bind="attrs" v-on="on"
										></v-text-field>
									</template>
									<v-date-picker locale="fr" color="#c62828" v-model="form.date" @input="dateMenu = false"></v-date-picker>
								</v-menu>
							</v-col>
							<v-col cols="6">
								<v-menu v-model="timeMenu" :close-on-content-click="false">
									<template v-slot:activator="{ on, attrs }">
										<v-text-field
											v-model="form.time"
											label="Heure"
											outlined dense rounded
											prepend-inner-icon="mdi-clock-outline"
											readonly
											:rules="[v => !!v || 'Champ requis']"
											hide-details="auto"
											v-bind="attrs" v-on="on"
										></v-text-field>
									</template>
									<v-time-picker format="24hr" color="#c62828" v-model="form.time" @click:minute="timeMenu = false"></v-time-picker>
								</v-menu>
							</v-col>
						</v-row>
						<div class="mt-4">
							<p class="field-lbl">Récurrence</p>
							<v-btn-toggle v-model="form.recurrence" mandatory dense class="recurrence-toggle">
								<v-btn value="none" small class="recurrence-btn">Aucune</v-btn>
								<v-btn value="daily" small class="recurrence-btn">Quotidien</v-btn>
								<v-btn value="weekly" small class="recurrence-btn">Hebdo</v-btn>
								<v-btn value="monthly" small class="recurrence-btn">Mensuel</v-btn>
								<v-btn value="yearly" small class="recurrence-btn">Annuel</v-btn>
							</v-btn-toggle>
						</div>
					</v-form>
				</v-card-text>
				<v-card-actions class="pa-4 pt-1">
					<template v-if="editedId && currentEvent">
						<v-btn icon small class="mr-1" @click="confirmArchive(currentEvent)">
							<v-icon size="18" color="#6b7280">mdi-archive-outline</v-icon>
						</v-btn>
						<v-btn icon small @click="confirmDelete(currentEvent)">
							<v-icon size="18" color="#c62828">mdi-delete</v-icon>
						</v-btn>
					</template>
					<v-spacer></v-spacer>
					<v-btn text rounded color="grey darken-1" @click="closeDialog">Annuler</v-btn>
					<v-btn :disabled="!valid" depressed rounded class="cal-save-btn" @click="save">Enregistrer</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- ─── Dialog : Archiver ─── -->
		<v-dialog v-model="dialogArchive" max-width="420px">
			<v-card rounded="xl" class="pa-2">
				<div class="del-header">
					<div class="del-icon" style="background:#6b7280;">
						<v-icon size="15" color="white">mdi-archive</v-icon>
					</div>
					<div>
						<p class="del-title">{{ archiveTarget && archiveTarget.archived ? 'Restaurer ce rappel ?' : 'Archiver ce rappel ?' }}</p>
						<p class="del-sub">{{ archiveTarget ? archiveTarget.title : '' }}</p>
					</div>
				</div>
				<v-card-actions class="pa-4 pt-2">
					<v-btn text rounded color="grey" @click="dialogArchive = false">Annuler</v-btn>
					<v-spacer></v-spacer>
					<v-btn depressed rounded class="arch-confirm-btn" @click="doArchive">
						{{ archiveTarget && archiveTarget.archived ? 'Restaurer' : 'Archiver' }}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- ─── Dialog : Supprimer ─── -->
		<v-dialog v-model="dialogDelete" max-width="420px">
			<v-card rounded="xl" class="pa-2">
				<div class="del-header">
					<div class="del-icon"><v-icon size="15" color="white">mdi-alert</v-icon></div>
					<div>
						<p class="del-title">Supprimer ce rappel ?</p>
						<p class="del-sub">{{ deleteTarget ? deleteTarget.title : '' }}</p>
					</div>
				</div>
				<v-card-text class="pb-0">
					<p class="del-warn">Cette action est irréversible.</p>
				</v-card-text>
				<v-card-actions class="pa-4 pt-2">
					<v-btn text rounded color="grey" @click="dialogDelete = false">Annuler</v-btn>
					<v-spacer></v-spacer>
					<v-btn depressed rounded class="del-confirm-btn" @click="doDelete">Supprimer</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</section>
</template>

<script>
import Loader from "@/components/Loader.vue";
import FloatingAlert from "@/components/FloatingAlert.vue";
import service from "../services";
import notifier from "@/utils/reminderNotifier";

export default {
	components: { Loader, FloatingAlert },
	data: () => ({
		loaderDialog: false,
		alertMessage: "",
		alertShow: false,
		alertType: "",

		// Calendrier
		viewDate: new Date(),
		viewMode: "month",
		loading: false,
		events: [],

		// Dialogues
		dialog: false,
		dialogArchive: false,
		dialogDelete: false,
		archiveTarget: null,
		deleteTarget: null,
		currentEvent: null,

		// Formulaire
		valid: false,
		dateMenu: false,
		timeMenu: false,
		editedId: null,
		form: { title: "", description: "", salle: null, date: "", time: "", recurrence: "none" },
		defaultForm: { title: "", description: "", salle: null, date: "", time: "", recurrence: "none" },

		dayNames: ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"],
	}),

	computed: {
		venueOptions() { return this.$store.getters.venues || []; },

		viewTitle() {
			const months = ["Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"];
			const d = this.viewDate;
			if (this.viewMode === "month") {
				return `${months[d.getMonth()]} ${d.getFullYear()}`;
			} else if (this.viewMode === "week") {
				const days = this.weekDays;
				if (!days.length) return "";
				const s = days[0];
				const e = days[6];
				if (s.getMonth() === e.getMonth()) {
					return `${s.getDate()} – ${e.getDate()} ${months[s.getMonth()]} ${s.getFullYear()}`;
				}
				return `${s.getDate()} ${months[s.getMonth()]} – ${e.getDate()} ${months[e.getMonth()]} ${e.getFullYear()}`;
			} else {
				const daysFr = ["Dimanche","Lundi","Mardi","Mercredi","Jeudi","Vendredi","Samedi"];
				return `${daysFr[d.getDay()]} ${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
			}
		},

		monthGrid() {
			const d = this.viewDate;
			const firstDay = new Date(d.getFullYear(), d.getMonth(), 1);
			const dow = firstDay.getDay();
			const offset = dow === 0 ? 6 : dow - 1;
			const start = new Date(firstDay);
			start.setDate(start.getDate() - offset);
			const cells = [];
			for (let i = 0; i < 42; i++) {
				const cell = new Date(start);
				cell.setDate(start.getDate() + i);
				cells.push(cell);
			}
			return cells;
		},

		weekDays() {
			const d = new Date(this.viewDate);
			const dow = d.getDay();
			const offset = dow === 0 ? 6 : dow - 1;
			const monday = new Date(d);
			monday.setDate(d.getDate() - offset);
			const days = [];
			for (let i = 0; i < 7; i++) {
				const day = new Date(monday);
				day.setDate(monday.getDate() + i);
				days.push(day);
			}
			return days;
		},

		dayEvents() {
			return this.eventsForDate(this.viewDate).slice().sort((a, b) => (a.time || "").localeCompare(b.time || ""));
		},
	},

	watch: {
		viewMode() { this.loadEvents(); },
		viewDate() { this.loadEvents(); },
		dialog(val) { val || this.closeDialog(); },
	},

	created() { this.loadEvents(); },

	methods: {
		getViewRange() {
			if (this.viewMode === "month") {
				const cells = this.monthGrid;
				return { start: cells[0], end: cells[41] };
			} else if (this.viewMode === "week") {
				const days = this.weekDays;
				return { start: days[0], end: days[6] };
			} else {
				const start = new Date(this.viewDate);
				start.setHours(0, 0, 0, 0);
				const end = new Date(this.viewDate);
				end.setHours(23, 59, 59, 999);
				return { start, end };
			}
		},

		loadEvents() {
			const range = this.getViewRange();
			this.loading = true;
			service.getRemindersByRange({
				startDate: range.start.toISOString(),
				endDate: range.end.toISOString(),
			}).then(r => {
				this.events = r.data?.rappels || [];
				this.loading = false;
			}).catch(e => { console.log(e); this.loading = false; });
		},

		occursOn(event, date) {
			if (!event.date) return false;
			const base = new Date(event.date);
			base.setHours(0, 0, 0, 0);
			const d = new Date(date);
			d.setHours(0, 0, 0, 0);
			if (d < base) return false;
			switch (event.recurrence) {
				case "daily": return true;
				case "weekly": return base.getDay() === d.getDay();
				case "monthly": return base.getDate() === d.getDate();
				case "yearly": return base.getMonth() === d.getMonth() && base.getDate() === d.getDate();
				default: return base.getTime() === d.getTime();
			}
		},

		eventsForDate(date) {
			return this.events.filter(e => this.occursOn(e, date));
		},

		navigate(dir) {
			const d = new Date(this.viewDate);
			if (this.viewMode === "month") d.setMonth(d.getMonth() + dir);
			else if (this.viewMode === "week") d.setDate(d.getDate() + dir * 7);
			else d.setDate(d.getDate() + dir);
			this.viewDate = d;
		},

		goToday() { this.viewDate = new Date(); },

		drillDown(date) {
			this.viewDate = new Date(date);
			this.viewMode = "day";
		},

		isToday(date) {
			const t = new Date();
			return date.getDate() === t.getDate() && date.getMonth() === t.getMonth() && date.getFullYear() === t.getFullYear();
		},

		isOverdue(event) {
			if (!event.date || !event.time) return false;
			const evDate = new Date(`${event.date.split("T")[0]}T${event.time}`);
			return evDate < new Date();
		},

		shortDayName(date) {
			return ["Dim","Lun","Mar","Mer","Jeu","Ven","Sam"][date.getDay()];
		},

		formatFullDate(date) {
			const days = ["Dimanche","Lundi","Mardi","Mercredi","Jeudi","Vendredi","Samedi"];
			const months = ["janvier","février","mars","avril","mai","juin","juillet","août","septembre","octobre","novembre","décembre"];
			return `${days[date.getDay()]} ${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
		},

		recurrenceLabel(r) {
			return { daily: "Quotidien", weekly: "Hebdomadaire", monthly: "Mensuel", yearly: "Annuel" }[r] || r;
		},

		manageAlert(show, type, msg) { this.alertShow = show; this.alertType = type; this.alertMessage = msg; },

		openCreate(date) {
			this.editedId = null;
			this.currentEvent = null;
			const d = date instanceof Date ? new Date(date) : new Date(date);
			const now = new Date();
			const hh = String(now.getHours()).padStart(2, "0");
			const mm = String(now.getMinutes()).padStart(2, "0");
			this.form = { ...Object.assign({}, this.defaultForm), date: d.toISOString().split("T")[0], time: `${hh}:${mm}` };
			this.dialog = true;
			this.$nextTick(() => { if (this.$refs.form) this.$refs.form.resetValidation(); });
		},

		openEdit(event) {
			this.editedId = event._id;
			this.currentEvent = event;
			this.form = {
				title: event.title || "",
				description: event.description || "",
				salle: event.salle?._id || event.salle || null,
				date: event.date ? event.date.split("T")[0] : "",
				time: event.time || "",
				recurrence: event.recurrence || "none",
			};
			this.dialog = true;
			this.$nextTick(() => { if (this.$refs.form) this.$refs.form.resetValidation(); });
		},

		closeDialog() {
			this.dialog = false;
			this.editedId = null;
			this.currentEvent = null;
			this.$nextTick(() => {
				this.form = Object.assign({}, this.defaultForm);
				if (this.$refs.form) this.$refs.form.resetValidation();
			});
		},

		save() {
			if (!this.$refs.form.validate()) return;
			this.loaderDialog = true;
			const isNew = !this.editedId;
			const savedId = this.editedId;
			const savedForm = { ...this.form };
			const p = isNew
				? service.createReminder(this.form)
				: service.updateReminder(this.editedId, this.form);
			p.then(r => {
				this.loaderDialog = false;
				this.manageAlert(true, "success", "Rappel enregistré !");
				if (isNew) {
					const created = r.data?.rappel;
					if (created) notifier.scheduleReminder(created);
				} else {
					notifier.cancelReminder(savedId);
					notifier.scheduleReminder({ _id: savedId, ...savedForm });
				}
				this.closeDialog();
				this.loadEvents();
			}).catch(e => { this.loaderDialog = false; this.manageAlert(true, "error", e?.message || "Erreur"); });
		},

		confirmArchive(event) {
			this.archiveTarget = event;
			this.dialogArchive = true;
		},

		doArchive() {
			this.loaderDialog = true;
			service.archiveReminder(this.archiveTarget._id)
				.then(() => {
					this.loaderDialog = false;
					this.dialogArchive = false;
					this.dialog = false;
					this.archiveTarget = null;
					this.manageAlert(true, "success", "Rappel archivé !");
					this.loadEvents();
				})
				.catch(e => { this.loaderDialog = false; this.manageAlert(true, "error", e?.message || "Erreur"); });
		},

		confirmDelete(event) {
			this.deleteTarget = event;
			this.dialogDelete = true;
		},

		doDelete() {
			this.loaderDialog = true;
			service.deleteReminder(this.deleteTarget._id)
				.then(() => {
					this.loaderDialog = false;
					this.dialogDelete = false;
					this.dialog = false;
					notifier.cancelReminder(this.deleteTarget._id);
					this.deleteTarget = null;
					this.manageAlert(true, "success", "Rappel supprimé !");
					this.loadEvents();
				})
				.catch(e => { this.loaderDialog = false; this.manageAlert(true, "error", e?.message || "Erreur"); });
		},
	},
};
</script>

<style scoped>
/* ─── En-tête ─── */
.cal-header { display: flex; align-items: center; padding: 14px 20px; gap: 12px; flex-wrap: wrap; }
.cal-header-left { display: flex; align-items: center; gap: 14px; min-width: 180px; }
.cal-icon-box { width: 38px; height: 38px; border-radius: 10px; background: #c62828; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.cal-title { font-size: 0.92rem; font-weight: 700; color: #111827; margin: 0 0 2px; font-family: Montserrat, sans-serif; }
.cal-subtitle { font-size: 0.72rem; color: #9ca3af; margin: 0; font-family: Montserrat, sans-serif; }
.cal-nav { display: flex; align-items: center; gap: 2px; }
.cal-today-btn { font-family: Montserrat, sans-serif !important; font-weight: 600 !important; font-size: 0.78rem !important; text-transform: none !important; border: 1px solid #e5e7eb; border-radius: 8px !important; }
.cal-view-toggle { border-radius: 8px !important; }
.cal-view-btn { font-family: Montserrat, sans-serif !important; font-size: 0.75rem !important; font-weight: 600 !important; text-transform: none !important; letter-spacing: 0 !important; }
.cal-add-btn { background: #c62828 !important; color: white !important; font-family: Montserrat, sans-serif; font-weight: 700; font-size: 0.8rem; box-shadow: 0 4px 12px rgba(198,40,40,0.28) !important; }
.cal-loading { display: flex; justify-content: center; padding: 60px; }

/* ─── Vue Mois ─── */
.cal-month { font-family: Montserrat, sans-serif; }
.cal-day-headers { display: grid; grid-template-columns: repeat(7, 1fr); border-bottom: 1px solid #f3f4f6; }
.cal-day-header { padding: 8px 10px; font-size: 0.7rem; font-weight: 700; color: #9ca3af; text-transform: uppercase; letter-spacing: 0.5px; text-align: center; }
.cal-grid { display: grid; grid-template-columns: repeat(7, 1fr); }
.cal-cell { min-height: 100px; border-right: 1px solid #f3f4f6; border-bottom: 1px solid #f3f4f6; padding: 6px; cursor: pointer; transition: background 0.15s; }
.cal-cell:nth-child(7n) { border-right: none; }
.cal-cell:hover { background: #fafafa; }
.cal-cell-other-month { background: #fafafa; }
.cal-cell-today { background: #fff5f5; }
.cal-cell-num { display: inline-flex; align-items: center; justify-content: center; width: 24px; height: 24px; font-size: 0.78rem; font-weight: 700; color: #374151; margin-bottom: 4px; border-radius: 50%; cursor: pointer; }
.cal-cell-num:hover { background: #f3f4f6; }
.cal-cell-num-today { background: #c62828; color: white; }
.cal-cell-events { display: flex; flex-direction: column; gap: 2px; }
.cal-ev-chip { background: #dbeafe; color: #1e40af; border-radius: 4px; padding: 2px 5px; font-size: 0.67rem; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; cursor: pointer; transition: opacity 0.15s; font-family: Montserrat, sans-serif; }
.cal-ev-chip:hover { opacity: 0.8; }
.cal-ev-chip-overdue { background: #fee2e2; color: #991b1b; }
.cal-ev-more { font-size: 0.65rem; font-weight: 700; color: #6b7280; padding: 1px 4px; cursor: pointer; }
.cal-ev-more:hover { color: #c62828; }

/* ─── Vue Semaine ─── */
.cal-week { font-family: Montserrat, sans-serif; }
.cal-week-grid { display: grid; grid-template-columns: repeat(7, 1fr); }
.cal-week-col { border-right: 1px solid #f3f4f6; }
.cal-week-col:last-child { border-right: none; }
.cal-week-col-today { background: #fff5f5; }
.cal-week-col-head { display: flex; flex-direction: column; align-items: center; padding: 10px 6px 8px; border-bottom: 1px solid #f3f4f6; cursor: pointer; gap: 4px; }
.cal-week-col-head:hover { background: #f9fafb; }
.cal-week-dow { font-size: 0.65rem; font-weight: 700; color: #9ca3af; text-transform: uppercase; letter-spacing: 0.5px; }
.cal-week-dnum { font-size: 0.88rem; font-weight: 800; color: #374151; width: 28px; height: 28px; display: flex; align-items: center; justify-content: center; border-radius: 50%; }
.cal-week-dnum-today { background: #c62828; color: white; }
.cal-week-events { padding: 6px 4px; min-height: 120px; display: flex; flex-direction: column; gap: 3px; }
.cal-week-ev { background: #dbeafe; border-radius: 5px; padding: 4px 6px; cursor: pointer; transition: opacity 0.15s; }
.cal-week-ev:hover { opacity: 0.8; }
.cal-week-ev-overdue { background: #fee2e2; }
.cal-week-ev-time { display: block; font-size: 0.62rem; font-weight: 700; color: #1e40af; margin-bottom: 1px; }
.cal-week-ev-title { display: block; font-size: 0.72rem; font-weight: 600; color: #1e3a8a; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.cal-week-ev-overdue .cal-week-ev-time { color: #991b1b; }
.cal-week-ev-overdue .cal-week-ev-title { color: #7f1d1d; }
.cal-week-no-ev { font-size: 0.72rem; color: #d1d5db; text-align: center; padding-top: 12px; }

/* ─── Vue Jour ─── */
.cal-day-view { font-family: Montserrat, sans-serif; }
.cal-day-head { padding: 14px 20px 10px; }
.cal-day-head-label { font-size: 0.9rem; font-weight: 700; color: #374151; margin: 0; text-transform: capitalize; }
.cal-day-empty { display: flex; flex-direction: column; align-items: center; padding: 60px 20px; gap: 8px; }
.cal-day-empty-text { font-size: 0.88rem; font-weight: 700; color: #6b7280; margin: 0; }
.cal-day-list { padding: 0 0 12px; }
.cal-day-ev { display: flex; align-items: flex-start; gap: 14px; padding: 14px 20px; border-bottom: 1px solid #f3f4f6; transition: background 0.15s; }
.cal-day-ev:hover { background: #fafafa; }
.cal-day-ev-overdue { border-left: 3px solid #ef4444; }
.cal-day-ev-time-col { min-width: 50px; flex-shrink: 0; padding-top: 2px; }
.cal-day-ev-time { font-size: 0.8rem; font-weight: 700; color: #6b7280; font-family: 'Courier New', monospace; }
.cal-day-ev-body { flex: 1; }
.cal-day-ev-title { font-size: 0.88rem; font-weight: 700; color: #111827; margin: 0 0 3px; }
.cal-day-ev-desc { font-size: 0.76rem; color: #6b7280; margin: 0 0 6px; }
.cal-day-ev-meta { display: flex; align-items: center; flex-wrap: wrap; gap: 4px; }
.cal-day-ev-actions { display: flex; align-items: center; gap: 2px; flex-shrink: 0; }

/* ─── Form dialog ─── */
.form-dialog-header { display: flex; align-items: center; gap: 14px; padding: 18px 20px 14px; }
.form-dialog-icon { width: 34px; height: 34px; border-radius: 8px; display: flex; align-items: center; justify-content: center; }
.form-dialog-title { font-size: 0.88rem; font-weight: 700; color: #111827; margin: 0 0 2px; font-family: Montserrat, sans-serif; }
.form-dialog-sub { font-size: 0.68rem; color: #9ca3af; margin: 0; font-family: Montserrat, sans-serif; }
.field-lbl { font-size: 0.72rem; font-weight: 700; color: #6b7280; font-family: Montserrat, sans-serif; margin: 0 0 8px; text-transform: uppercase; letter-spacing: 0.5px; }
.recurrence-toggle { flex-wrap: wrap; border-radius: 8px !important; }
.recurrence-btn { font-family: Montserrat, sans-serif !important; font-size: 0.72rem !important; font-weight: 600 !important; text-transform: none !important; letter-spacing: 0 !important; }
.cal-save-btn { background: #c62828 !important; color: white !important; font-family: Montserrat, sans-serif; font-weight: 700; font-size: 0.82rem; box-shadow: 0 4px 12px rgba(198,40,40,0.28) !important; }

/* ─── Dialogs confirmation ─── */
.del-header { display: flex; align-items: center; gap: 12px; padding: 16px 20px 12px; }
.del-icon { width: 34px; height: 34px; border-radius: 8px; background: #c62828; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.del-title { font-size: 0.9rem; font-weight: 700; color: #111827; margin: 0 0 2px; font-family: Montserrat, sans-serif; }
.del-sub { font-size: 0.72rem; color: #9ca3af; margin: 0; font-family: Montserrat, sans-serif; }
.del-warn { font-size: 0.78rem; color: #6b7280; font-family: Montserrat, sans-serif; background: #fef2f2; border: 1px solid #fee2e2; border-radius: 8px; padding: 10px 12px; margin: 0; }
.del-confirm-btn { background: #b91c1c !important; color: white !important; font-family: Montserrat, sans-serif; font-weight: 700; font-size: 0.82rem; }
.arch-confirm-btn { background: #6b7280 !important; color: white !important; font-family: Montserrat, sans-serif; font-weight: 700; font-size: 0.82rem; }
</style>
